import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import AddButton from "components/AddButton";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";
import EditButton from "components/EditButton";
import MaterialTableTS from "components/tables/MaterialTableTS";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { PERMISSIONS } from "modules/PermissionsConstants";
import Timer from "components/Timer";
import TableFilters from "components/TableFilters";
import {
  addStateOfficeAccount,
  editStateOfficeAccount,
  officeAccountFetching,
  officeAccountFilterText,
  officeAccountReFetching,
  rowEditOfficeAccount,
  selectedRowsStateOfficeAccount,
} from "../atoms";
import OfficeAccountForm from "../forms/OfficeAccountForm";
import useDisableOfficeAccount from "../hooks/useDisableOfficeAccount";
import useGetOfficeAccount from "../hooks/useGetOfficeAccount";
import { OfficeAccountType, OfficeAccountTypeTableData } from "../types";
import { toast } from "components/toastNotification";

export default function OfficeAccountTable() {
  const { data: directData, refetch: reFetch } = useGetOfficeAccount();

  const BulkDelete = useDisableOfficeAccount();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(
    addStateOfficeAccount
  );
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateOfficeAccount
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateOfficeAccount
  );
  const [filterTextState, setFilterTextState] = useRecoilState(
    officeAccountFilterText
  );
  const setEditRowData = useSetRecoilState(rowEditOfficeAccount);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(officeAccountFetching);
  const isReFetching = useRecoilValue(officeAccountReFetching);

  //Local States
  const [tableData, setTableData] = useState<OfficeAccountType[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<
    OfficeAccountType[]
  >([]);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officeaccount_add;
  const editPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officeaccount_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officeaccount_delete;

  // Get Data
  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status == 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  //For filtered data update

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: OfficeAccountType[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].accountNumber);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  // For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.accountNumber &&
        item.accountNumber
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );

  useEffect(() => {
    if (!recent) setTableFilteredData(filtereditems);
  }, [filterTextState]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: OfficeAccountTypeTableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });

    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: OfficeAccountTypeTableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);

    const ids = selectedRows && selectedRows.map((row) => row.accountId);

    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Office Accounts" : "Office Account"
    }?`}</div>
  );

  const matColumns = [
    {
      title: "Office Account",
      field: "accountNumber",
      customSort: (a, b) =>
        a.accountNumber && b.accountNumber
          ? a.accountNumber.trim().localeCompare(b.accountNumber.trim())
          : a.accountNumber
          ? 0
          : -1,
    },
    {
      title: "Office Bank",
      field: "officeBankName",
      customSort: (a, b) =>
        a.officeBankName && b.officeBankName
          ? a.officeBankName.trim().localeCompare(b.officeBankName.trim())
          : a.officeBankName
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },

    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center mr-5">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="bg-white p-4">
        {actionAddState || actionEditState ? (
          <div>
            <OfficeAccountForm setRecent={setRecent} />
          </div>
        ) : (
          <div className="">
            <div className="bg-white space-y-2">
              <div className="bg-white w-ful">
                <div className="flex flex-row justify-between items-center">
                  <div className="">
                    <TableFilters
                      defaultText={filterTextState}
                      recentText={recent}
                      setRecent={setRecent}
                      textInputName={"accountNumber"}
                      columnDetails={"Office Account"}
                      filterText={setFilterTextState}
                      disabledButton={
                        actionEditState ||
                        (selectedRows !== undefined && selectedRows.length > 0)
                      }
                      disabledTextBox={
                        selectedRows !== undefined && selectedRows.length > 0
                      }
                    />
                  </div>
                  <div className="">
                    {userPermissions.includes(addPermission) ? (
                      <AddButton
                        buttonText="New Office Account"
                        onClick={() => handleAdd()}
                        disabled={
                          actionEditState ||
                          (selectedRows !== undefined &&
                            selectedRows.length > 0)
                        }
                      />
                    ) : (
                      ""
                    )}
                  </div>
                </div>
                <div>
                  {userPermissions.includes(deletePermission) ? (
                    <div className="text-secondary italic text-xs3 my-2">
                      <p className="font-bold italic inline-block mr-1">
                        {" "}
                        Note:{" "}
                      </p>
                      <span>Tick the box to deactivate some data.</span>
                    </div>
                  ) : (
                    <div className="mb-4"></div>
                  )}
                </div>
                <div>
                  <MaterialTableTS
                    data={
                      isFetching
                        ? []
                        : filterTextState === "" && recent === ""
                        ? tableData
                        : filterTextState === ""
                        ? tableFilteredData
                        : filtereditems
                    }
                    showTitle={true}
                    columns={matColumns}
                    isLoading={isFetching}
                    onSelectionChange={handleSelectedRowsChange}
                    selectedRows={selectedRows}
                    selection={
                      !actionEditState &&
                      !actionAddState &&
                      userPermissions.includes(deletePermission)
                    }
                    setConfirmModalOpen={setOpenModalDelete}
                  />
                </div>
              </div>
            </div>
            <ConfirmBoxDelete
              ModalTitle="Deactivate Office Account"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
                setSelectedRows([]);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </div>
        )}
      </div>
    </>
  );
}
